/**
 * 20-10-2026 (Jordy): een verkoop zonder gekoppeld gesprek. De fan-naam opent
 * een lijst KANDIDAAT-gesprekken van hetzelfde model (zelfde naam, een
 * ontgrendeling rond de verkoop, of een open betaald bericht met een passende
 * prijs), met per kandidaat de chatter en het tijdstip. Alleen een hulp om het
 * gesprek te vinden: toewijzen gaat via de bestaande CHATTER-keuze.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from './Modal'
import { ErrorNote, Loading, Tag } from './ui'
import { useApi } from '../lib/useApi'
import { dtTx, eur } from '../lib/format'

export interface KandidaatSignaal {
  kind: 'ontgrendeling' | 'open_ppv' | 'open_ppv_afwijkend'
  at_utc: string | null
  price: number | null
  price_matches: boolean | null
  chatter: string | null
}
export interface Kandidaat {
  conversation_id: number
  fan_id: string | null
  fan_name: string | null
  matched_by: Array<'naam' | 'ontgrendeling' | 'open_ppv' | 'open_ppv_afwijkend'>
  signals: KandidaatSignaal[]
  last_chatter_before_sale: { chatter: string | null; at_utc: string | null } | null
  last_fan_message_at_utc: string | null
}
export interface Kandidaten {
  sale_at_utc: string | null
  buyer_names: string[]
  window_unlock_minutes: number
  window_open_ppv_hours: number
  candidates: Kandidaat[]
  more: number
  reason: string | null
}

const LABEL: Record<string, string> = {
  naam: 'Same name',
  ontgrendeling: 'Unlock near the sale',
  open_ppv: 'Paid message, matching price',
  // 23-10-2026 (Jordy): zwakker dan een passende prijs (mogelijke omrekening van dollars).
  open_ppv_afwijkend: 'Paid message, price differs',
}

function signaalTekst(s: KandidaatSignaal): string {
  const prijs = s.price != null ? eur(s.price) : 'paid message'
  const wie = s.chatter ?? 'unknown sender'
  return s.kind === 'ontgrendeling'
    ? `${prijs} unlocked ${dtTx(s.at_utc)} · sent by ${wie}${s.price_matches ? '' : ' (price differs)'}`
    : `${prijs} sent ${dtTx(s.at_utc)} by ${wie} · not marked as opened${s.kind === 'open_ppv_afwijkend' ? ' (price differs)' : ''}`
}

export function KandidaatLijst({ data, op, onOpen }: { data: Kandidaten; op: string | null; onOpen?: () => void }) {
  if (!data.candidates.length) {
    return <div className="mut2" data-testid="geen-kandidaten" style={{ fontSize: 13 }}>{data.reason}</div>
  }
  return (
    <div data-testid="kandidaten" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8 }}>
      {data.candidates.map((k) => (
        <div key={k.conversation_id} className="card" data-testid="kandidaat" data-id={k.conversation_id}
             style={{ padding: '8px 10px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3,
                      overflowWrap: 'anywhere' }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b>{k.fan_name || k.fan_id || `Chat ${k.conversation_id}`}</b>
            <span className="mut2 mono" style={{ fontSize: 11 }}>{k.fan_id}</span>
            <span style={{ flex: 1 }} />
            <Link className="btn sm" to={`/conversations/show/${k.conversation_id}${op ? `?op=${encodeURIComponent(op)}` : ''}`}
                  onClick={onOpen}>Open chat</Link>
          </div>
          <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
            {k.matched_by.map((m) => (
              <Tag key={m} kind={m === 'naam' || m === 'open_ppv_afwijkend' ? 'mut' : 'ok'}>{LABEL[m] ?? m}</Tag>
            ))}
          </div>
          {k.signals.map((s, n) => (
            <div key={n} style={{ fontSize: 12.5 }}>{signaalTekst(s)}</div>
          ))}
          <div className="mut2" style={{ fontSize: 12 }}>
            Last chatter before the sale:{' '}
            {k.last_chatter_before_sale
              ? <><b>{k.last_chatter_before_sale.chatter ?? 'unknown'}</b> · {dtTx(k.last_chatter_before_sale.at_utc)}</>
              : '—'}
            {' · '}Last fan message: {dtTx(k.last_fan_message_at_utc)}
          </div>
        </div>
      ))}
      {data.more > 0 ? <div className="mut2" style={{ fontSize: 12 }}>+{data.more} more with the same name (oldest activity).</div> : null}
    </div>
  )
}

function KandidaatDialoog({ eventId, op, titel, onClose, onOpen }: {
  eventId: number; op: string | null; titel: ReactNode; onClose: () => void; onOpen?: () => void
}) {
  const q = useApi<Kandidaten>(`/api/admin/dashboard/transactions/${eventId}/candidate-chats`, [eventId])
  return (
    <Modal title="Find the chat of this sale" width={620} onClose={onClose}
           desc={<>
             Sale {dtTx(op)} · buyer <b>{titel}</b>. No chat is linked automatically. These chats of the same
             model could be it: open one, check who sold, then pick the chatter in the CHATTER column.
           </>}>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      {q.data ? <KandidaatLijst data={q.data} op={op} onOpen={onOpen} /> : null}
    </Modal>
  )
}

/** De fan-naam als knop die de kandidaten opent. */
export function KandidaatKnop({ eventId, op, children, onOpen }: {
  eventId: number; op: string | null; children: ReactNode; onOpen?: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="linkbtn" data-testid="geen-gesprek"
              title="No linked chat. Click to see candidate chats of this model."
              style={{ font: 'inherit', fontWeight: 'inherit', padding: 0, textAlign: 'left' }}
              onClick={() => setOpen(true)}>
        {children}
      </button>
      {/* In body, niet in de tabelcel (die erft nowrap en tabel-klikken). */}
      {open ? createPortal(<KandidaatDialoog eventId={eventId} op={op} titel={children}
                                             onClose={() => setOpen(false)} onOpen={onOpen} />, document.body) : null}
    </>
  )
}
